import {
  CTCSS_TONES,
  DTCS_CODES,
  decodeLbcd,
  encodeLbcd,
  compareImages,
  type RadioProgress,
  type WebChannel,
} from './baofeng-web.ts'
import type { RadioTransport } from './web-serial.ts'

const ACK = 0x06
const READ_BLOCK_SIZE = 0x40
const WRITE_BLOCK_SIZE = 0x10
const MEMORY_SIZE = 0x4000
const UPLOAD_SIZE = 0x3100
const CHANNEL_COUNT = 200
const CHANNEL_SIZE = 16
const NAME_OFFSET = 0x1000
const NAME_SIZE = 16

const BTECH_DTCS = [...DTCS_CODES, 645].sort((left, right) => left - right)

type BTechSpec = {
  id: string
  vendor: string
  model: string
  summary: string
  magic: readonly number[]
  fileIds: readonly string[]
  color?: boolean
  readBlockSize?: 64 | 16
  powerLevels?: readonly string[]
  channels?: number
  nameLength?: 6 | 7 | 8
  powerBits?: 1 | 2
  uploadSize?: number
}

export type BTechMobileProfile = {
  protocol: 'btech'
  id: string
  vendor: string
  model: string
  summary: string
  tags: readonly string[]
  baudRate: 9600
  channelMinimum: 0
  channelMaximum: number
  nameLength: 6 | 7 | 8
  powerLevels: readonly string[]
  memorySize: number
  magic: readonly number[]
  fileIds: readonly (readonly number[])[]
  color: boolean
  readBlockSize: 64 | 16
  powerBits: 1 | 2
  uploadSize: number
}

function ascii(value: string): readonly number[] {
  return Array.from(value, (character) => character.charCodeAt(0) & 0xff)
}

function fromHex(value: string): readonly number[] {
  return Array.from({ length: value.length / 2 }, (_, index) =>
    Number.parseInt(value.slice(index * 2, index * 2 + 2), 16),
  )
}

function createProfile(spec: BTechSpec): BTechMobileProfile {
  return {
    protocol: 'btech',
    id: spec.id,
    vendor: spec.vendor,
    model: spec.model,
    summary: spec.summary,
    tags: [
      'Experimental',
      'Read + write',
      `${spec.channels ?? 200} channels`,
      `${spec.nameLength ?? (spec.color ? 8 : 6)}-character names`,
    ],
    baudRate: 9600,
    channelMinimum: 0,
    channelMaximum: (spec.channels ?? 200) - 1,
    nameLength: spec.nameLength ?? (spec.color ? 8 : 6),
    powerLevels: spec.powerLevels ?? ['High', 'Low'],
    memorySize: MEMORY_SIZE,
    magic: spec.magic,
    fileIds: spec.fileIds.map(ascii),
    color: spec.color ?? false,
    readBlockSize: spec.readBlockSize ?? READ_BLOCK_SIZE,
    powerBits: spec.powerBits ?? (spec.color ? 2 : 1),
    uploadSize: spec.uploadSize ?? UPLOAD_SIZE,
  }
}

const STANDARD_MAGIC = fromHex('5520150920454d02')
const DUAL_BAND_220_MAGIC = fromHex('5520151212014d02')
const MINI_8900_MAGIC = fromHex('55a5b54555454d02')
const KT8900_MAGIC = fromHex('5520150916454d02')
const KT8900R_MAGIC = fromHex('5520150925014d02')
const UV25X2_MAGIC = fromHex('5520161228ffdc02')
const UV25X4_MAGIC = fromHex('5520161118ffdc02')
const KT8900D_MAGIC = fromHex('5520160801ffdc02')
const KT8R_MAGIC = fromHex('5520170703ffdc02')
const KTWP12_MAGIC = fromHex('5520181102ffdc02')
const KT5000_MAGIC = fromHex('5520210123ffdc02')
const GMRS50_MAGIC = fromHex('5520181018ffdc02')
const GMRS20V2_MAGIC = fromHex('5520210327ffdc02')

export const BTECH_MOBILE_PROFILES: readonly BTechMobileProfile[] = [
  createProfile({
    id: 'btech-uv-2501',
    vendor: 'BTECH',
    model: 'UV-2501',
    summary: 'BTECH UV-2501 dual-band mobile with 200 memory channels.',
    magic: STANDARD_MAGIC,
    fileIds: ['BTG324', 'BTG214', 'M29204', 'M2C294'],
  }),
  createProfile({
    id: 'btech-uv-2501-220',
    vendor: 'BTECH',
    model: 'UV-2501+220',
    summary: 'BTECH UV-2501+220 tri-band mobile.',
    magic: DUAL_BAND_220_MAGIC,
    fileIds: ['BTG311', 'BTG211', 'M3G201', 'M3C281'],
  }),
  createProfile({
    id: 'btech-uv-5001',
    vendor: 'BTECH',
    model: 'UV-5001',
    summary: 'BTECH UV-5001 high-power dual-band mobile.',
    magic: STANDARD_MAGIC,
    fileIds: ['BTG304', 'V2G204', 'BTG214', 'V28204', 'V19204'],
  }),
  createProfile({
    id: 'waccom-mini-8900',
    vendor: 'WACCOM',
    model: 'MINI-8900',
    summary: 'WACCOM Mini-8900 compact mobile radio.',
    magic: MINI_8900_MAGIC,
    fileIds: ['M28854'],
  }),
  createProfile({
    id: 'qyt-kt-uv980',
    vendor: 'QYT',
    model: 'KT-UV980',
    summary: 'QYT KT-UV980 dual-band mobile radio.',
    magic: MINI_8900_MAGIC,
    fileIds: ['H28854'],
  }),
  createProfile({
    id: 'qyt-kt8900',
    vendor: 'QYT',
    model: 'KT8900',
    summary: 'QYT KT8900 dual-band mobile family.',
    magic: KT8900_MAGIC,
    fileIds: [
      'M29154',
      'M2C234',
      'M2G1F4',
      'M2G2F4',
      'M2G304',
      'M2G314',
      'M2G424',
      'M27184',
      'M2C194',
    ],
  }),
  createProfile({
    id: 'qyt-kt8900r',
    vendor: 'QYT',
    model: 'KT8900R',
    summary: 'QYT KT8900R tri-band mobile radio.',
    magic: KT8900R_MAGIC,
    fileIds: ['M3G1F4', 'M3G214', 'M3C234', 'M39164', 'M3G314', 'M3B064'],
  }),
  createProfile({
    id: 'luiton-lt-588uv',
    vendor: 'LUITON',
    model: 'LT-588UV',
    summary: 'LUITON LT-588UV dual-band mobile radio.',
    magic: KT8900_MAGIC,
    fileIds: ['V2G1F4', 'V2G214'],
  }),
  createProfile({
    id: 'jetstream-jt2705m',
    vendor: 'Jetstream',
    model: 'JT2705M',
    summary: 'Jetstream JT2705M using the classic KT-UV980 protocol.',
    magic: MINI_8900_MAGIC,
    fileIds: ['H28854'],
  }),
  createProfile({
    id: 'juentai-jt-6188-plus',
    vendor: 'Juentai',
    model: 'JT-6188 Plus',
    summary: 'Juentai JT-6188 Plus using the Mini-8900 protocol.',
    magic: MINI_8900_MAGIC,
    fileIds: ['M28854'],
  }),
  createProfile({
    id: 'juentai-jt-6188-mini',
    vendor: 'Juentai',
    model: 'JT-6188 Mini',
    summary: 'Juentai JT-6188 Mini using the KT8900 protocol.',
    magic: KT8900_MAGIC,
    fileIds: [
      'M29154',
      'M2C234',
      'M2G1F4',
      'M2G2F4',
      'M2G304',
      'M2G314',
      'M2G424',
      'M27184',
      'M2C194',
    ],
  }),
  createProfile({
    id: 'sainsonic-gt-890',
    vendor: 'Sainsonic',
    model: 'GT-890',
    summary: 'Sainsonic GT-890 using the KT8900 protocol.',
    magic: KT8900_MAGIC,
    fileIds: [
      'M29154',
      'M2C234',
      'M2G1F4',
      'M2G2F4',
      'M2G304',
      'M2G314',
      'M2G424',
      'M27184',
      'M2C194',
    ],
  }),
  createProfile({
    id: 'zastone-mp-300',
    vendor: 'Zastone',
    model: 'MP-300',
    summary: 'Zastone MP-300 using the KT8900 protocol.',
    magic: KT8900_MAGIC,
    fileIds: [
      'M29154',
      'M2C234',
      'M2G1F4',
      'M2G2F4',
      'M2G304',
      'M2G314',
      'M2G424',
      'M27184',
      'M2C194',
    ],
  }),
  createProfile({
    id: 'btech-uv-25x2',
    vendor: 'BTECH',
    model: 'UV-25X2',
    summary: 'BTECH UV-25X2 color-screen dual-band mobile.',
    magic: UV25X2_MAGIC,
    fileIds: ['UC2012'],
    color: true,
  }),
  createProfile({
    id: 'btech-uv-25x2-g2',
    vendor: 'BTECH',
    model: 'UV-25X2_G2',
    summary: 'Second-generation BTECH UV-25X2 color-screen mobile.',
    magic: UV25X2_MAGIC,
    fileIds: ['UCB282'],
    color: true,
  }),
  createProfile({
    id: 'btech-uv-25x4',
    vendor: 'BTECH',
    model: 'UV-25X4',
    summary: 'BTECH UV-25X4 quad-band color-screen mobile.',
    magic: UV25X4_MAGIC,
    fileIds: ['UC4014'],
    color: true,
  }),
  createProfile({
    id: 'btech-uv-25x4-g2',
    vendor: 'BTECH',
    model: 'UV-25X4_G2',
    summary: 'Second-generation BTECH UV-25X4 quad-band mobile.',
    magic: UV25X4_MAGIC,
    fileIds: ['UCB284'],
    color: true,
  }),
  createProfile({
    id: 'btech-uv-50x2',
    vendor: 'BTECH',
    model: 'UV-50X2',
    summary: 'BTECH UV-50X2 high-power color-screen mobile.',
    magic: UV25X2_MAGIC,
    fileIds: ['UC2M12'],
    color: true,
  }),
  createProfile({
    id: 'btech-uv-50x2-g2',
    vendor: 'BTECH',
    model: 'UV-50X2_G2',
    summary: 'Second-generation BTECH UV-50X2 high-power mobile.',
    magic: UV25X2_MAGIC,
    fileIds: ['UC2M12'],
    color: true,
  }),
  createProfile({
    id: 'qyt-kt7900d',
    vendor: 'QYT',
    model: 'KT7900D',
    summary: 'QYT KT7900D quad-band color-screen mobile.',
    magic: KT8900D_MAGIC,
    fileIds: [
      'VC4004',
      'VC4284',
      'VC4264',
      'VC4114',
      'VC4104',
      'VC4254',
      'VC5264',
      'VC9204',
      'VC9214',
      'VC5302',
      'VC5254',
      'QB-25',
    ],
    color: true,
  }),
  createProfile({
    id: 'qyt-kt8900d',
    vendor: 'QYT',
    model: 'KT8900D',
    summary: 'QYT KT8900D color-screen dual-band mobile.',
    magic: KT8900D_MAGIC,
    fileIds: ['VC2002', 'VC8632', 'VC3402', 'VC7062', 'VC3062', 'VC8192'],
    color: true,
    readBlockSize: 16,
  }),
  createProfile({
    id: 'qyt-kt5800',
    vendor: 'QYT',
    model: 'KT5800',
    summary: 'QYT KT5800 color-screen dual-band mobile.',
    magic: KT8900D_MAGIC,
    fileIds: ['VCB222'],
    color: true,
  }),
  createProfile({
    id: 'qyt-kt980plus',
    vendor: 'QYT',
    model: 'KT980PLUS',
    summary: 'QYT KT980PLUS high-power color-screen mobile.',
    magic: KT8900D_MAGIC,
    fileIds: ['VC2002', 'VC6042'],
    color: true,
  }),
  createProfile({
    id: 'radioddity-db25-g',
    vendor: 'Radioddity',
    model: 'DB25-G',
    summary: 'Radioddity DB25-G GMRS mobile with three power levels.',
    magic: KT8900D_MAGIC,
    fileIds: ['VC6182', 'VC7062'],
    color: true,
    powerLevels: ['High', 'Mid', 'Low'],
  }),
  createProfile({
    id: 'surecom-s-kt8900d',
    vendor: 'Surecom',
    model: 'S-KT8900D',
    summary: 'Surecom S-KT8900D clone of the KT7900D platform.',
    magic: KT8900D_MAGIC,
    fileIds: [
      'VC4004',
      'VC4284',
      'VC4264',
      'VC4114',
      'VC4104',
      'VC4254',
      'VC5264',
      'VC9204',
      'VC9214',
      'VC5302',
      'VC5254',
    ],
    color: true,
  }),
  createProfile({
    id: 'radioddity-qb25',
    vendor: 'Radioddity',
    model: 'QB25',
    summary: 'Radioddity QB25 clone of the KT7900D platform.',
    magic: KT8900D_MAGIC,
    fileIds: ['QB-25'],
    color: true,
  }),
  createProfile({
    id: 'otgstuff-otg-radio-v1',
    vendor: 'OTGSTUFF',
    model: 'OTG Radio v1',
    summary: 'OTG Radio v1 using the KT8900D platform.',
    magic: KT8900D_MAGIC,
    fileIds: ['VC2002', 'VC8632', 'VC3402', 'VC7062', 'VC3062', 'VC8192'],
    color: true,
  }),
  createProfile({
    id: 'qyt-kt-8r',
    vendor: 'QYT',
    model: 'KT-8R',
    summary: 'QYT KT-8R quad-band color handheld.',
    magic: KT8R_MAGIC,
    fileIds: ['MCB264', 'MCB284', 'MC5264', 'MCB254'],
    color: true,
    nameLength: 8,
    powerBits: 1,
  }),
  createProfile({
    id: 'qyt-kt-wp12',
    vendor: 'QYT',
    model: 'KT-WP12',
    summary: 'QYT KT-WP12 compact color mobile radio.',
    magic: KTWP12_MAGIC,
    fileIds: ['WP3094'],
    color: true,
    nameLength: 7,
  }),
  createProfile({
    id: 'anysecu-wp-9900',
    vendor: 'Anysecu',
    model: 'WP-9900',
    summary: 'Anysecu WP-9900 compact color mobile radio.',
    magic: KTWP12_MAGIC,
    fileIds: ['WP3094'],
    color: true,
    nameLength: 7,
  }),
  createProfile({
    id: 'qyt-kt-5000',
    vendor: 'QYT',
    model: 'KT-5000',
    summary: 'QYT KT-5000 compact color mobile radio.',
    magic: KT5000_MAGIC,
    fileIds: ['WP9114'],
    color: true,
    nameLength: 7,
  }),
  createProfile({
    id: 'btech-gmrs-50x1',
    vendor: 'BTECH',
    model: 'GMRS-50X1',
    summary: 'BTECH GMRS-50X1 with 256 channels and three power levels.',
    magic: GMRS50_MAGIC,
    fileIds: ['NC1802', 'NC1932'],
    color: true,
    channels: 256,
    nameLength: 7,
    powerBits: 2,
    powerLevels: ['High', 'Mid', 'Low'],
    uploadSize: 0x3400,
  }),
  createProfile({
    id: 'btech-gmrs-50v2',
    vendor: 'BTECH',
    model: 'GMRS-50V2',
    summary: 'Second-generation BTECH GMRS-50V2 mobile.',
    magic: GMRS50_MAGIC,
    fileIds: ['NC1802', 'NC1932'],
    color: true,
    channels: 256,
    nameLength: 7,
    powerBits: 2,
    powerLevels: ['High', 'Mid', 'Low'],
    uploadSize: 0x3400,
  }),
  createProfile({
    id: 'btech-gmrs-20v2',
    vendor: 'BTECH',
    model: 'GMRS-20V2',
    summary: 'BTECH GMRS-20V2 compact GMRS mobile.',
    magic: GMRS20V2_MAGIC,
    fileIds: ['WP4204', 'VW2112', 'VWG728'],
    color: true,
    nameLength: 7,
    powerBits: 2,
    powerLevels: ['High', 'Mid', 'Low'],
  }),
]

function emptyChannel(
  number: number,
  profile: BTechMobileProfile,
): WebChannel {
  return {
    number,
    empty: true,
    name: '',
    rxFrequency: null,
    txFrequency: null,
    toneMode: 'none',
    ctcss: null,
    dtcs: null,
    dtcsPolarity: 'N',
    mode: 'FM',
    power: profile.powerLevels[0],
    skip: false,
  }
}

function decodeName(raw: Uint8Array): string {
  let end = raw.length
  for (let index = 0; index < raw.length; index += 1) {
    if (raw[index] === 0xff || raw[index] === 0) {
      end = index
      break
    }
  }
  return new TextDecoder('ascii')
    .decode(raw.slice(0, end))
    .replace(/[^\x20-\x7e]/g, '')
    .trimEnd()
}

function decodeTone(raw: Uint8Array): Pick<
  WebChannel,
  'toneMode' | 'ctcss' | 'dtcs' | 'dtcsPolarity'
> {
  const value = raw[0] | (raw[1] << 8)
  if (value === 0 || value === 0xffff) {
    return { toneMode: 'none', ctcss: null, dtcs: null, dtcsPolarity: 'N' }
  }
  if (value > 0x0258) {
    return {
      toneMode: 'ctcss',
      ctcss: value / 10,
      dtcs: null,
      dtcsPolarity: 'N',
    }
  }
  const reversed = value > 0x69
  const index = reversed ? value - 0x6a : value - 1
  return {
    toneMode: 'dtcs',
    ctcss: null,
    dtcs: BTECH_DTCS[index] ?? BTECH_DTCS[0],
    dtcsPolarity: reversed ? 'R' : 'N',
  }
}

function encodeTone(channel: WebChannel): readonly [number, number] {
  let value = 0
  if (channel.toneMode === 'ctcss' && channel.ctcss) {
    if (!CTCSS_TONES.includes(channel.ctcss as (typeof CTCSS_TONES)[number])) {
      throw new Error('That CTCSS tone is not supported by this radio.')
    }
    value = Math.round(channel.ctcss * 10)
  } else if (channel.toneMode === 'dtcs' && channel.dtcs) {
    const index = BTECH_DTCS.indexOf(channel.dtcs)
    if (index < 0) throw new Error('That DCS code is not supported by this radio.')
    value = index + 1 + (channel.dtcsPolarity === 'R' ? 0x69 : 0)
  }
  return [value & 0xff, (value >> 8) & 0xff]
}

export function parseBTechMobileChannel(
  image: Uint8Array,
  profile: BTechMobileProfile,
  number: number,
): WebChannel {
  const offset = (number - profile.channelMinimum) * CHANNEL_SIZE
  const raw = image.slice(offset, offset + CHANNEL_SIZE)
  const rxFrequency = decodeLbcd(raw.slice(0, 4))
  if (raw.byteLength !== CHANNEL_SIZE || !rxFrequency) {
    return emptyChannel(number, profile)
  }
  const nameOffset =
    NAME_OFFSET + (number - profile.channelMinimum) * NAME_SIZE
  return {
    number,
    empty: false,
    name: decodeName(image.slice(nameOffset, nameOffset + profile.nameLength)),
    rxFrequency,
    txFrequency: decodeLbcd(raw.slice(4, 8)),
    ...decodeTone(raw.slice(8, 10)),
    mode: (raw[15] & 0x40) !== 0 ? 'NFM' : 'FM',
    power:
      profile.powerLevels[
        raw[14] & (profile.powerBits === 2 ? 0x03 : 0x01)
      ] ?? profile.powerLevels[0],
    skip: (raw[15] & 0x04) === 0,
  }
}

export function parseBTechMobileChannels(
  image: Uint8Array,
  profile: BTechMobileProfile,
): WebChannel[] {
  return Array.from(
    { length: profile.channelMaximum - profile.channelMinimum + 1 },
    (_, index) =>
      parseBTechMobileChannel(
        image,
        profile,
        index + profile.channelMinimum,
      ),
  )
}

export function applyBTechMobileChannel(
  image: Uint8Array,
  profile: BTechMobileProfile,
  channel: WebChannel,
): void {
  const offset = (channel.number - profile.channelMinimum) * CHANNEL_SIZE
  const nameOffset =
    NAME_OFFSET + (channel.number - profile.channelMinimum) * NAME_SIZE
  const raw = image.subarray(offset, offset + CHANNEL_SIZE)
  if (channel.empty) {
    raw.fill(0xff)
    image.subarray(nameOffset, nameOffset + NAME_SIZE).fill(0xff)
    return
  }
  if (!channel.rxFrequency) throw new Error('A receive frequency is required.')
  const wasEmpty = raw[0] === 0xff
  const preserved14 =
    wasEmpty ? 0 : raw[14] & (profile.powerBits === 2 ? 0xfc : 0xfe)
  const preserved15 = wasEmpty ? 0 : raw[15] & 0xbb
  if (wasEmpty) raw.fill(0)
  encodeLbcd(channel.rxFrequency, raw.subarray(0, 4))
  if (channel.txFrequency) {
    encodeLbcd(channel.txFrequency, raw.subarray(4, 8))
  } else {
    raw.subarray(4, 8).fill(0xff)
  }
  const tone = encodeTone(channel)
  raw.set(tone, 8)
  raw.set(tone, 10)
  const powerIndex = Math.max(0, profile.powerLevels.indexOf(channel.power))
  raw[14] =
    preserved14 | (powerIndex & (profile.powerBits === 2 ? 0x03 : 0x01))
  raw[15] =
    preserved15 |
    (channel.mode === 'NFM' ? 0x40 : 0) |
    (channel.skip ? 0 : 0x04)
  const encodedName = new Uint8Array(NAME_SIZE).fill(0xff)
  encodedName.set(
    new TextEncoder().encode(
      channel.name.toUpperCase().slice(0, profile.nameLength),
    ),
  )
  image.set(encodedName, nameOffset)
}

function containsBytes(haystack: Uint8Array, needle: Uint8Array): boolean {
  for (let start = 0; start <= haystack.length - needle.length; start += 1) {
    if (needle.every((value, index) => haystack[start + index] === value)) {
      return true
    }
  }
  return false
}

async function enterProgrammingMode(
  profile: BTechMobileProfile,
  transport: RadioTransport,
): Promise<string> {
  await transport.discardInput()
  await transport.write(Uint8Array.from(profile.magic))
  const identity = await transport.readExact(50, 3000)
  if (
    identity[0] !== ACK ||
    !profile.fileIds.some((fileId) =>
      containsBytes(identity, Uint8Array.from(fileId)),
    )
  ) {
    throw new Error(
      `The connected radio does not identify as ${profile.vendor} ${profile.model}.`,
    )
  }
  await new Promise((resolve) => setTimeout(resolve, 100))
  return new TextDecoder('ascii')
    .decode(identity)
    .replace(/[^\x20-\x7e]/g, '')
    .trim()
}

function commandFrame(
  command: string,
  address: number,
  size: number,
): Uint8Array {
  return Uint8Array.of(
    ACK,
    command.charCodeAt(0),
    (address >> 8) & 0xff,
    address & 0xff,
    size,
  )
}

async function readBlock(
  profile: BTechMobileProfile,
  transport: RadioTransport,
  address: number,
): Promise<Uint8Array> {
  await transport.write(commandFrame('S', address, profile.readBlockSize))
  const response = await transport.readExact(profile.readBlockSize + 5, 3000)
  if (
    ![ACK, 0x05].includes(response[0]) ||
    response[1] !== 'X'.charCodeAt(0) ||
    ((response[2] << 8) | response[3]) !== address ||
    response[4] !== profile.readBlockSize
  ) {
    throw new Error(
      `Unexpected BTECH read reply at 0x${address.toString(16)}.`,
    )
  }
  return response.slice(5)
}

async function writeBlock(
  transport: RadioTransport,
  address: number,
  data: Uint8Array,
): Promise<void> {
  const frame = new Uint8Array(5 + WRITE_BLOCK_SIZE)
  frame.set(commandFrame('X', address, WRITE_BLOCK_SIZE))
  frame.set(data, 5)
  await transport.write(frame)
  const firstAck = await transport.readExact(1, 2500)
  if (firstAck[0] !== ACK) {
    const secondAck = await transport.readExact(1, 2500)
    if (secondAck[0] !== ACK) {
      throw new Error(
        `The radio rejected write block 0x${address.toString(16)}.`,
      )
    }
  }
}

export async function readBTechMobileRadioWithTransport(
  profile: BTechMobileProfile,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<{ image: Uint8Array; reportedModel: string }> {
  const reportedModel = await enterProgrammingMode(profile, transport)
  const image = new Uint8Array(profile.memorySize)
  const total = profile.memorySize / profile.readBlockSize
  for (let index = 0; index < total; index += 1) {
    const address = index * profile.readBlockSize
    image.set(await readBlock(profile, transport, address), address)
    onProgress?.({
      current: index + 1,
      total,
      label: 'Reading BTECH/QYT mobile memory',
    })
  }
  await transport.write(Uint8Array.of(ACK))
  return { image, reportedModel: reportedModel || profile.model }
}

export async function writeBTechMobileRadioWithTransport(
  profile: BTechMobileProfile,
  image: Uint8Array,
  transport: RadioTransport,
  onProgress?: (progress: RadioProgress) => void,
): Promise<void> {
  if (image.byteLength < profile.memorySize) {
    throw new Error('The selected image is smaller than this radio memory map.')
  }
  await enterProgrammingMode(profile, transport)
  const total = profile.uploadSize / WRITE_BLOCK_SIZE
  for (let index = 0; index < total; index += 1) {
    const address = index * WRITE_BLOCK_SIZE
    await writeBlock(
      transport,
      address,
      image.slice(address, address + WRITE_BLOCK_SIZE),
    )
    onProgress?.({
      current: index + 1,
      total,
      label: 'Writing BTECH/QYT mobile memory',
    })
  }
}
